import themeColorCssMap from '@/lib/theme-color-css-map.json';
import { convertColorToOklch, type ClientThemeColors } from '@/types/theme';

export type FlatThemeColorValues = ClientThemeColors;

const FLAT_COLOR_TO_CSS_VAR = themeColorCssMap as Record<string, string>;

/** CSS vars managed by useClientTheme inline light-mode application. */
export const MANAGED_THEME_COLOR_CSS_VARS = [
  ...Object.values(FLAT_COLOR_TO_CSS_VAR),
  '--sidebar-primary',
];

function formatThemeColor(color: string): string {
  return convertColorToOklch(color);
}

/** Matches static `.dark.tenant-*` rules in generated tenant-themes.css. */
export function darkThemeCssSelector(clientId: string): string {
  const tenant = clientId.trim().toLowerCase();
  return `.dark.tenant-${tenant}, .tenant-${tenant}.dark`;
}

function buildDarkThemeCssLines(
  colors: FlatThemeColorValues,
  formatColor: (color: string) => string,
): string[] {
  const lines: string[] = [];

  for (const [key, cssVar] of Object.entries(FLAT_COLOR_TO_CSS_VAR)) {
    const value = colors[key as keyof ClientThemeColors]?.trim();
    if (value) lines.push(`  ${cssVar}: ${formatColor(value)};`);
  }

  const primary = colors.primary?.trim();
  if (primary) {
    lines.push(`  --sidebar-primary: ${formatColor(primary)};`);
  }

  const primaryDark = colors.primaryDark?.trim();
  if (primaryDark) {
    lines.push(`  --primary-dark: ${formatColor(primaryDark)};`);
  }

  return lines;
}

export function applyFlatThemeColorsToRoot(
  root: HTMLElement,
  colors: FlatThemeColorValues,
  formatColor: (color: string) => string = formatThemeColor,
): void {
  for (const [key, cssVar] of Object.entries(FLAT_COLOR_TO_CSS_VAR)) {
    const value = colors[key as keyof ClientThemeColors]?.trim();
    if (value) root.style.setProperty(cssVar, formatColor(value));
  }

  const primary = colors.primary?.trim();
  if (primary) {
    root.style.setProperty('--sidebar-primary', formatColor(primary));
  }

  const primaryDark = colors.primaryDark?.trim();
  if (primaryDark) {
    root.style.setProperty('--primary-dark', formatColor(primaryDark));
  } else if (primary) {
    root.style.setProperty('--primary-dark', formatColor(primary));
  }
}

export function flatThemeColorsToDarkCss(
  colors: FlatThemeColorValues,
  options: {
    clientId: string;
    formatColor?: (color: string) => string;
  },
): string {
  const formatColor = options.formatColor ?? formatThemeColor;
  const lines = buildDarkThemeCssLines(colors, formatColor);
  const selector = darkThemeCssSelector(options.clientId);
  return `${selector} {\n${lines.join('\n')}\n}`;
}
